/**
 * Referral Tracking Utility
 *
 * Captures and stores referral metadata (UTM parameters, referral codes) from URL query params.
 * Persists data in localStorage until the user submits a case or deal.
 *
 * Server-side persistence (Issue #1223 PR3 GAP 13):
 * - On first capture, also POSTs attribution to the backend so it survives
 *   browser localStorage clear.
 * - Provides re-hydration from server when localStorage is empty.
 *
 * Usage:
 * - Call captureReferralMetadata() on app initialization
 * - Call getReferralMetadata() when creating cases/deals
 * - Call clearReferralMetadata() after successful submission
 */

import { env } from '../../config/env';

const STORAGE_KEY = 'opsmatrix_referral_metadata';
const SESSION_KEY = 'opsmatrix_attribution_session';
const REHYDRATION_CHECKED_KEY = 'opsmatrix_attribution_checked';

export interface ReferralMetadata {
  // UTM Parameters (standard marketing tracking)
  utm_source?: string; // Traffic source (e.g., "google", "facebook", "partner_site")
  utm_medium?: string; // Marketing medium (e.g., "cpc", "email", "social")
  utm_campaign?: string; // Campaign name (e.g., "spring_sale", "product_launch")
  utm_term?: string; // Paid search keywords
  utm_content?: string; // A/B testing content variants

  // Custom referral tracking
  referralCode?: string; // Partner referral code
  referralSource?: string; // Custom referral source
  trackingCode?: string; // Tracking code (emp_, ref_, ptr_) from invite links

  // Metadata
  referrer?: string; // HTTP referrer (previous page URL)
  landingPage?: string; // First page user landed on
  capturedAt?: string; // ISO timestamp when metadata was captured
}

/**
 * Capture referral metadata from current URL query parameters and referrer.
 * Stores data in localStorage for later retrieval.
 *
 * @returns Captured referral metadata or null if no referral data found
 */
export function captureReferralMetadata(): ReferralMetadata | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const metadata: ReferralMetadata = {};

    // Capture UTM parameters
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    const utmTerm = params.get('utm_term');
    const utmContent = params.get('utm_content');

    if (utmSource) metadata.utm_source = utmSource;
    if (utmMedium) metadata.utm_medium = utmMedium;
    if (utmCampaign) metadata.utm_campaign = utmCampaign;
    if (utmTerm) metadata.utm_term = utmTerm;
    if (utmContent) metadata.utm_content = utmContent;

    // Capture custom referral codes (support multiple formats)
    const referralCode = params.get('ref') || params.get('referralCode') || params.get('referral_code');
    if (referralCode) {
      metadata.referralCode = referralCode;
    }

    // Capture custom referral source
    const referralSource = params.get('referralSource') || params.get('referral_source');
    if (referralSource) {
      metadata.referralSource = referralSource;
    }

    // Capture tracking code from invite links (?tc=emp_xxx)
    const trackingCode = params.get('tc') || params.get('trackingCode') || params.get('tracking_code');
    if (trackingCode && /^[a-z]{2,5}_[a-zA-Z0-9]{6,20}$/.test(trackingCode)) {
      metadata.trackingCode = trackingCode;
    }

    // Capture HTTP referrer (only if from external site)
    if (document.referrer && !document.referrer.includes(window.location.hostname)) {
      metadata.referrer = document.referrer;
    }

    // Capture landing page
    metadata.landingPage = window.location.pathname + window.location.search;

    // Add timestamp
    metadata.capturedAt = new Date().toISOString();

    // Only store if we have any referral data
    const hasReferralData = Object.keys(metadata).length > 2; // More than just landingPage + capturedAt
    if (hasReferralData) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(metadata));
      return metadata;
    }

    return null;
  } catch (error) {
    console.error('Failed to capture referral metadata:', error);
    return null;
  }
}

/**
 * Retrieve stored referral metadata from localStorage.
 *
 * @returns Stored referral metadata or null if not found
 */
export function getReferralMetadata(): ReferralMetadata | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const metadata = JSON.parse(stored) as ReferralMetadata;
    return metadata;
  } catch (error) {
    console.error('Failed to retrieve referral metadata:', error);
    return null;
  }
}

/**
 * Clear referral metadata from localStorage.
 * Call this after successfully submitting a case or deal.
 */
export function clearReferralMetadata(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear referral metadata:', error);
  }
}

/**
 * Check if referral metadata exists in localStorage.
 *
 * @returns true if referral metadata is stored
 */
export function hasReferralMetadata(): boolean {
  return getReferralMetadata() !== null;
}

// ==================== Server-Side Persistence (Issue #1223 PR3 GAP 13) ====================

/**
 * Get or create a stable session ID for attribution lookup.
 * Stored in localStorage so it persists across browser sessions and visits,
 * enabling server-side rehydration even after tab close/reopen (AC13.3).
 */
function generateSessionId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Fallback for environments without randomUUID
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function getSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;

    const id = generateSessionId();
    localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return generateSessionId();
  }
}

/**
 * POST referral metadata to the server for persistence.
 * Fire-and-forget: does not block or throw on failure.
 *
 * @param data - Referral metadata to persist
 * @param email - Optional consumer email for reliable re-hydration
 */
export function persistReferralToServer(data: ReferralMetadata, email?: string): void {
  try {
    const sessionId = getSessionId();
    const payload = {
      email: email || undefined,
      sessionId,
      referralCode: data.referralCode,
      trackingCode: data.trackingCode,
      utmSource: data.utm_source,
      utmMedium: data.utm_medium,
      utmCampaign: data.utm_campaign,
      utmTerm: data.utm_term,
      utmContent: data.utm_content,
      landingPage: data.landingPage,
      referrer: data.referrer,
    };

    fetch(`${env.VITE_API_URL}/trpc/attribution.save?batch=1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ '0': { json: payload } }),
    }).catch(() => {
      // Silent failure — localStorage is the primary store
    });
  } catch {
    // Silent failure — never block page interactions
  }
}

/**
 * Re-hydrate referral metadata from the server if localStorage is empty.
 * Returns the re-hydrated data or null if nothing found.
 *
 * @param email - Consumer email to look up
 * @returns Referral metadata from server, or null
 */
export async function rehydrateReferralFromServer(email?: string): Promise<ReferralMetadata | null> {
  try {
    // Skip if already checked this session
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(REHYDRATION_CHECKED_KEY)) {
      return null;
    }

    // Already have localStorage data — no need to re-hydrate
    if (getReferralMetadata()) return getReferralMetadata();

    const sessionId = getSessionId();
    // The server's getAttributionSchema requires a non-empty sessionId. In
    // exceptional environments where crypto APIs are absent or localStorage
    // is replaced with a stub returning falsy values, getSessionId can yield
    // an empty string. Bail early rather than firing a request that the
    // gateway will reject — there is nothing to re-hydrate without a stable id.
    if (!sessionId) return null;

    // Mark as checked before the request so this only runs once per session
    // even if fetch rejects (network error)
    try {
      sessionStorage.setItem(REHYDRATION_CHECKED_KEY, '1');
    } catch {}

    // Use POST to keep email out of URL (PII should not appear in query strings
    // where it can be logged by proxies, CDNs, APM traces, and browser history)
    const input = JSON.stringify({
      '0': { json: { email: email || undefined, sessionId } },
    });

    const response = await fetch(`${env.VITE_API_URL}/trpc/attribution.get?batch=1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: input,
    });

    if (!response.ok) return null;

    const json = await response.json();
    // tRPC batch response format: [{ result: { data: { json: ... } } }]
    const result = json?.[0]?.result?.data?.json ?? json?.[0]?.result?.data;
    if (!result) return null;

    // Reconstruct ReferralMetadata from server response
    const metadata: ReferralMetadata = {};
    if (result.utmSource) metadata.utm_source = result.utmSource;
    if (result.utmMedium) metadata.utm_medium = result.utmMedium;
    if (result.utmCampaign) metadata.utm_campaign = result.utmCampaign;
    if (result.utmTerm) metadata.utm_term = result.utmTerm;
    if (result.utmContent) metadata.utm_content = result.utmContent;
    if (result.referralCode) metadata.referralCode = result.referralCode;
    if (result.trackingCode) metadata.trackingCode = result.trackingCode;
    if (result.landingPage) metadata.landingPage = result.landingPage;
    if (result.referrer) metadata.referrer = result.referrer;
    if (result.capturedAt) metadata.capturedAt = result.capturedAt;

    // Only re-hydrate if there is meaningful data
    const hasData = Object.keys(metadata).length > 1; // More than just capturedAt
    if (hasData) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(metadata));
      return metadata;
    }

    return null;
  } catch {
    return null;
  }
}
